import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import useSeo from "../hooks/useSeo";
import { SITE_NAME } from "../constants/seo";

export default function CityIndexPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [appliedKeyword, setAppliedKeyword] = useState("");
  const [level, setLevel] = useState("");
  const [loading, setLoading] = useState(true);
  const pageSize = 24;

  useSeo({
    title: `全国城市婚恋交友_同城找对象脱单 - ${SITE_NAME}`,
    metaDescription:
      "覆盖全国省市区县的同城找对象、脱单相亲、婚恋交友与婚恋咨询页面，AI智能匹配，专业咨询师免费陪伴，含婚姻咨询与婚姻修复服务。",
    ogDescription: "全国城市同城找对象、脱单、婚恋咨询索引。",
    keywords:
      "全国找对象,同城脱单,城市婚恋交友,同城相亲,婚恋咨询,婚姻修复,严肃婚恋",
    path: "/city",
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          page_size: String(pageSize),
        });
        if (appliedKeyword) params.set("keyword", appliedKeyword);
        if (level) params.set("level", level);

        const res = await fetch(`/api/public/city-seo?${params.toString()}`);
        const data = await res.json();
        if (res.ok) {
          setItems(data.items || []);
          setTotal(data.total || 0);
        }
      } catch (err) {
        console.error("加载城市列表失败:", err);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [page, appliedKeyword, level]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex flex-col">
      <Header />

      <main className="flex-1 px-4 py-10">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">全国城市服务</h1>
          <p className="text-gray-600 mb-6">
            选择所在城市，进入同城找对象、婚恋交友专题页（共 {total} 个已启用）。
          </p>

          <form
            className="bg-white rounded-xl shadow p-4 mb-6 flex flex-wrap gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setAppliedKeyword(keyword.trim());
            }}
          >
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索城市、省份、区县..."
              className="flex-1 min-w-[200px] border border-pink-100 rounded-xl px-4 py-2 text-sm"
            />
            <select
              value={level}
              onChange={(e) => {
                setLevel(e.target.value);
                setPage(1);
              }}
              className="border border-pink-100 rounded-xl px-3 py-2 text-sm"
            >
              <option value="">全部层级</option>
              <option value="province">省级</option>
              <option value="city">地级市</option>
              <option value="district">区县</option>
            </select>
            <button
              type="submit"
              className="px-5 py-2 bg-pink-500 text-white rounded-xl text-sm"
            >
              搜索
            </button>
          </form>

          {loading ? (
            <p className="text-center text-gray-400 py-12">加载中...</p>
          ) : items.length === 0 ? (
            <p className="text-center text-gray-400 py-12">暂无匹配城市</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {items.map((city) => (
                <Link
                  key={city.slug}
                  to={`/city/${city.slug}`}
                  className="block bg-white rounded-xl shadow p-5 hover:shadow-md border border-transparent hover:border-pink-200 transition"
                >
                  <h2 className="text-lg font-bold text-gray-800 mb-1">
                    {city.short_name}找对象
                  </h2>
                  <p className="text-sm text-gray-500">
                    {[city.province, city.city, city.district].filter(Boolean).join(" · ")}
                  </p>
                  <p className="text-xs text-pink-400 mt-2">
                    {city.level === "province"
                      ? "省级"
                      : city.level === "district"
                        ? "区县级"
                        : "城市级"}{" "}
                    · 婚恋交友
                  </p>
                </Link>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center gap-3 mt-8">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-4 py-2 rounded-lg border text-sm disabled:opacity-40"
              >
                上一页
              </button>
              <span className="text-sm text-gray-500 self-center">
                {page} / {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-4 py-2 rounded-lg border text-sm disabled:opacity-40"
              >
                下一页
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
